import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const ApprentiPrepTerms = () => {
  return (
    <div className="min-h-screen bg-white text-[#333333]">
      <Helmet>
        <title>Conditions d'utilisation et Politique de confidentialité - ApprentiPrep | Infinity Cloud</title>
        <meta name="description" content="Conditions d'utilisation et politique de confidentialité de l'application ApprentiPrep" />
      </Helmet>

      {/* Header avec navigation */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/apprentiprep" className="flex items-center space-x-2 text-[#0066cc] hover:opacity-80 transition-opacity">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Retour à ApprentiPrep</span>
            </Link>
            <Link to="/" className="text-[#666666] hover:text-[#0066cc] transition-colors">
              Accueil Infinity Cloud
            </Link>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            <span 
              style={{
                background: 'linear-gradient(135deg, #0066cc, #5ac8fa)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              ApprentiPrep
            </span>
          </h1>
          <p className="text-xl text-[#666666] mb-2">Conditions d'utilisation et Politique de confidentialité</p>
          <p className="text-[#666666] mb-8 italic">Dernière mise à jour : 1 juillet 2025</p>

          {/* Section 1 - Conditions d'utilisation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gray-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-4 text-[#0066cc]">1. Conditions d'utilisation</h2>
            
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">1.1 Acceptation des conditions</h3>
              <p className="text-[#666666] leading-relaxed">
                En téléchargeant et utilisant ApprentiPrep, vous acceptez ces conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'application.
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">1.2 Description du service</h3>
              <p className="text-[#666666] leading-relaxed">
                ApprentiPrep est une application de préparation aux tests d'aptitude pour l'apprentissage dual en Suisse. L'application propose des quiz, des explications et une assistance IA pour vous aider dans votre préparation.
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">1.3 Utilisation appropriée</h3>
              <p className="text-[#666666] leading-relaxed mb-3">Vous vous engagez à :</p>
              <ul className="space-y-2 text-[#666666]">
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Utiliser l'application à des fins éducatives uniquement</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Ne pas partager vos identifiants de connexion</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Ne pas tenter de contourner les mesures de sécurité</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Respecter les droits de propriété intellectuelle</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">1.4 Propriété intellectuelle</h3>
              <p className="text-[#666666] leading-relaxed">
                Tout le contenu de ApprentiPrep (questions, réponses, explications) est protégé par le droit d'auteur. © 2025 Infinity Cloud Sarl. Tous droits réservés.
              </p>
            </div>
          </motion.div>

          {/* Section 2 - Politique de confidentialité */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gray-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-4 text-[#0066cc]">2. Politique de confidentialité</h2>
            
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">2.1 Notre engagement</h3>
              <p className="text-[#666666] leading-relaxed">
                <strong>ApprentiPrep respecte absolument votre vie privée. Nous utilisons une IA 100% locale sur votre appareil. Aucune donnée personnelle n'est envoyée à des serveurs externes.</strong>
              </p>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">2.2 Données collectées</h3>
              <p className="text-[#666666] leading-relaxed mb-3">ApprentiPrep collecte uniquement :</p>
              <ul className="space-y-2 text-[#666666]">
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span><strong>Données de progression</strong> : Scores, questions répondues, temps d'utilisation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span><strong>Préférences</strong> : Paramètres de l'application, niveau choisi</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span><strong>Données de synchronisation</strong> : Si vous utilisez iCloud (optionnel)</span>
                </li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">2.3 Stockage des données</h3>
              <ul className="space-y-2 text-[#666666]">
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Toutes les données sont stockées localement sur votre appareil</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Si vous activez iCloud, les données sont synchronisées via votre compte Apple personnel</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Aucune donnée n'est envoyée à nos serveurs</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">2.4 Intelligence Artificielle</h3>
              <p className="text-[#666666] leading-relaxed mb-3">Notre IA fonctionne entièrement sur votre appareil :</p>
              <ul className="space-y-2 text-[#666666]">
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Aucune question ou réponse n'est envoyée à des serveurs externes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Le modèle IA est intégré dans l'application</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Prêt pour Apple Intelligence (septembre 2025)</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Section 3 - Vos choix de confidentialité */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-blue-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-4 text-[#0066cc]">3. Vos choix de confidentialité</h2>
            
            <div className="bg-white rounded-lg p-4 mb-4">
              <h3 className="text-lg font-semibold mb-3 text-[#333333]">Vous avez le contrôle total sur vos données :</h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-[#0066cc] mb-1">✓ Supprimer vos données</h4>
                  <p className="text-[#666666] text-sm">Dans l'application : Paramètres → Confidentialité → Effacer toutes les données</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-[#0066cc] mb-1">✓ Désactiver la synchronisation iCloud</h4>
                  <p className="text-[#666666] text-sm">Dans l'application : Paramètres → Synchronisation → Désactiver iCloud</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-[#0066cc] mb-1">✓ Exporter vos données</h4>
                  <p className="text-[#666666] text-sm">Dans l'application : Paramètres → Confidentialité → Exporter mes données</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-[#0066cc] mb-1">✓ Supprimer votre compte</h4>
                  <p className="text-[#666666] text-sm">Supprimez simplement l'application. Toutes les données locales seront effacées.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-[#333333]">3.1 Pas de tracking</h3>
              <p className="text-[#666666] leading-relaxed mb-3">ApprentiPrep ne contient :</p>
              <ul className="space-y-2 text-[#666666]">
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Aucun tracker publicitaire</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Aucun analytics tiers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Aucun cookie de suivi</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#0066cc] mr-2">•</span>
                  <span>Aucune collecte de données personnelles identifiables</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Section 4 - Sécurité */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-gray-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-3 text-[#0066cc]">4. Sécurité</h2>
            <p className="text-[#666666] leading-relaxed mb-3">Nous prenons la sécurité au sérieux :</p>
            <ul className="space-y-2 text-[#666666]">
              <li className="flex items-start">
                <span className="text-[#0066cc] mr-2">•</span>
                <span>Données chiffrées sur l'appareil</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0066cc] mr-2">•</span>
                <span>Synchronisation iCloud sécurisée par Apple</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0066cc] mr-2">•</span>
                <span>Aucune transmission de données sensibles</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#0066cc] mr-2">•</span>
                <span>Application régulièrement mise à jour</span>
              </li>
            </ul>
          </motion.div>

          {/* Section 5 - Modifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-gray-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-3 text-[#0066cc]">5. Modifications</h2>
            <p className="text-[#666666] leading-relaxed">
              Nous pouvons mettre à jour ces conditions. Les modifications seront notifiées dans l'application. Votre utilisation continue après les modifications constitue votre acceptation.
            </p>
          </motion.div>

          {/* Section 6 - Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-gray-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-3 text-[#0066cc]">6. Contact</h2>
            <p className="text-[#666666] leading-relaxed mb-3">
              Pour toute question concernant ces conditions ou votre confidentialité :
            </p>
            <div className="text-[#666666]">
              <p className="font-semibold">Infinity Cloud Sarl</p>
              <p>Email : <a href="mailto:mt@infinity-cloud.ch" className="text-[#0066cc] hover:underline">mt@infinity-cloud.ch</a></p>
              <p>Site web : <a href="https://www.infinity-cloud.ch" className="text-[#0066cc] hover:underline">www.infinity-cloud.ch</a></p>
            </div>
          </motion.div>

          {/* Section 7 - Juridiction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-gray-50 rounded-xl p-6 mb-6"
          >
            <h2 className="text-2xl font-semibold mb-3 text-[#0066cc]">7. Juridiction</h2>
            <p className="text-[#666666] leading-relaxed">
              Ces conditions sont régies par le droit suisse. Tout litige sera soumis aux tribunaux compétents de Genève, Suisse.
            </p>
          </motion.div>

          {/* Boutons d'action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 mt-12"
          >
            <Link 
              to="/apprentiprep"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#0066cc] to-[#5ac8fa] text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              Retour à ApprentiPrep
            </Link>
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#0066cc] font-semibold rounded-full shadow-lg hover:shadow-xl transition-all border-2 border-[#0066cc]"
            >
              Nous contacter
            </Link>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 mt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-[#666666]">
            <p className="mb-2">© 2025 Infinity Cloud Sarl. Tous droits réservés.</p>
            <p className="text-sm">
              ApprentiPrep est une marque déposée d'Infinity Cloud Sarl.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ApprentiPrepTerms;